/* eslint-disable react/prop-types */
import { Permission, UserPermissions, hasAnyPermission } from '@frontend/authorization';
import { ClassType, EntityType } from '@frontend/common';
import { Dropdown, DropdownItem, DropdownItemProps } from '@frontend/components/common';
import { ConfirmationModal } from '@frontend/components/modal';
import { ErrorHandler } from '@frontend/error-handler';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { CellContext, ColumnDef, HeaderContext, Row, createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { LuChevronDown, LuChevronRight, LuEllipsis } from 'react-icons/lu';

import { ObjectCellMapper } from './cells/object-cell-mapper';
import { Checkbox } from './table/selection/CheckBox';

export interface ObjectColumnsProps<T extends { id: string } & object> {
    listStoreId?: string;
    dispatch: ThunkDispatch<any, any, Action>;
    columns: (keyof T)[] | string[];
    showIdAsEntity?: EntityType;
    openModal?: ReturnType<typeof useModals>['openModal'];
    closeModal?: ReturnType<typeof useModals>['closeModal'];
    headerOverwrite?: Map<keyof T | string, React.ReactNode>;
    valueOverwrite?: Map<keyof T, keyof T>;
    /**
     * @deprecated
     * should use the document repository
     */
    blobColumnOverwrite?: Map<keyof T, (object: T) => Promise<Blob>>;
    documentColumnOverwrite?: {
        key: keyof T;
        entityType: EntityType;
    };
    /**
     * Should be used if no generic cell is available for the required functionality
     */
    customCells?: Map<string, (props: CellContext<T, any>) => React.ReactNode>;
    /**
     * the first provided key should contain the key holding the entity id
     * the second should hold the entity type
     */
    entityColumnOverwrite?: Map<keyof T, keyof T>;
    entityColumnOverwriteProps?: Map<keyof T, any>;
    hidden?: (keyof T | string)[];
    selectEnabled?: boolean;
    /**
     * @description keys should be provided in the correct order to use the delete function
     */
    customActionButtons?: (Omit<DropdownItemProps, 'onClick' | 'hide'> & { onClick: (obj: T) => void; hide?: ((obj: T) => boolean) | boolean })[];
    cellClassName?: string | ((row: Row<T>) => string);
    update?: {
        updateAction?: (object: T) => void;
        requiredPermissions?: Permission[];
    };
    delete?: {
        deleteAction?: (...args: any) => Promise<void>;
        deleteKeys?: (keyof T)[];
        displayName?: (value: T) => React.ReactNode;
        requiredPermissions?: Permission[];
    };
    permissions?: UserPermissions | null;
    expandEnabled?: (row: Row<T>) => boolean;
    onExpand?: (row: Row<T>) => void;
    onAllExpand?: (obj: T) => void;
    showExpandAll?: boolean;
    recovery?: {
        onRecover: (object: T) => void;
        requiredPermissions?: Permission[];
    };
    /** Called after a single-row delete succeeds so reactive lists can remove the row immediately. */
    onDeleteSuccess?: (object: T) => void;
    ignoreTranslations?: (keyof T)[];
}
export const defaultItemClassName = 'text-xs text-brand-primary align-content-center';
export const defaultCellClassName = 'text-foreground-default align-center items-center truncate px-4 py-3 text-sm font-normal';
export const defaultCellClassNameNoPadding = 'text-foreground-default align-center items-center truncate text-sm font-normal';

const DEFAULT_COLUMN_HEADERS = new Map<string, React.ReactNode>([
    ['heartbeat_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.HEARTBEAT_TIMESTAMP],
    ['version', CommonMessage.OBJECTS.COMMON_FIELDS.VERSION]
]);
/**
 * @param props
 * @returns
 */
export const ObjectColumns = <T extends { id: string } & object>(props: ObjectColumnsProps<T>): ColumnDef<T>[] => {
    return convert<T>(props);
};

function convert<T extends { id: string } & object>(props: ObjectColumnsProps<T>): ColumnDef<T>[] {
    const result: ColumnDef<T>[] = [];
    const columnHelper = createColumnHelper<T>();
    const { openModal, closeModal } = props;
    if (props.expandEnabled) {
        result.push(
            columnHelper.display({
                header: (headerProps: HeaderContext<T, unknown>) => {
                    if (!props.showExpandAll) return <td></td>;
                    return headerProps.table.getIsAllRowsExpanded() ? (
                        <LuChevronDown
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                headerProps.table.toggleAllRowsExpanded(false);
                            }}
                            className='cursor-pointer'
                        />
                    ) : (
                        <LuChevronRight
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                headerProps.table.getCoreRowModel().rows.forEach((row) => {
                                    const recursiveFetch = async (r: Row<T>) => {
                                        if (r.original && (r.original as any).children) {
                                            if ((r.original as any).children.length === 0) {
                                                return;
                                            }
                                            for (const child of (r.original as any).children) {
                                                props.onAllExpand && props.onAllExpand(child);
                                                await recursiveFetch(child);
                                            }
                                        }
                                    };
                                    recursiveFetch(row);
                                });
                                headerProps.table.toggleAllRowsExpanded(true);
                            }}
                            className='cursor-pointer'
                        />
                    );
                },
                id: 'expand',
                cell: (cellProps: CellContext<T, any>) => {
                    if (props.expandEnabled && !props.expandEnabled(cellProps.row)) return <td></td>;
                    return (
                        <td
                            style={{ width: '30px' }}
                            onClick={(e) => e.stopPropagation()}
                            key={cellProps.row.original.id}>
                            {cellProps.row.getIsExpanded() ? (
                                <LuChevronDown
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        cellProps.row.toggleExpanded(false);
                                    }}
                                    className='cursor-pointer'
                                />
                            ) : (
                                <LuChevronRight
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        props.onExpand && props.onExpand(cellProps.row);
                                        cellProps.row.toggleExpanded(true);
                                    }}
                                    className='cursor-pointer'
                                />
                            )}
                        </td>
                    );
                }
            })
        );
    }
    if (props.selectEnabled && props.listStoreId !== undefined) {
        result.push(
            columnHelper.display({
                header: (headerProps: HeaderContext<T, unknown>) => (
                    <div className='flex pl-4'>
                        <Checkbox
                            dispatch={props.dispatch}
                            listStoreId={props.listStoreId!}
                            {...headerProps.table.getToggleAllPageRowsSelectedHandler()}
                        />
                    </div>
                ),
                id: 'selection',
                cell: (cellProps: CellContext<T, any>) => {
                    return (
                        <td
                            className='w-0 pl-4'
                            onClick={(e) => e.stopPropagation()}
                            key={cellProps.row.original.id}>
                            <Checkbox
                                dispatch={props.dispatch}
                                listStoreId={props.listStoreId!}
                                original={cellProps.row.original}
                                {...cellProps.row.getToggleSelectedHandler()}
                            />
                        </td>
                    );
                },
                enableHiding: false
            })
        );
    }

    props.columns.forEach((col) => {
        if (props.hidden?.includes(col)) return;
        result.push({
            header: (headerProps: HeaderContext<T, unknown>) => {
                if (typeof col != 'string') return;
                if (props.headerOverwrite && props.headerOverwrite.has(col)) {
                    return props.headerOverwrite.get(col);
                }
                if (DEFAULT_COLUMN_HEADERS.has(col)) {
                    return DEFAULT_COLUMN_HEADERS.get(col);
                }
                return (col[0].toUpperCase() + col.slice(1)).replace(/_|timestamp/g, ' ');
            },
            id: col as string,
            cell: (cellProps: CellContext<T, any>) => (
                <ObjectCellMapper
                    className={props.cellClassName}
                    {...cellProps}
                    parent={props}
                    cellKey={col}
                />
            ),
            accessorKey: col as keyof T
        });
    });

    if (props.update || props.delete || props.customActionButtons || props.recovery) {
        const permissions = [
            ...(props.update?.requiredPermissions || []),
            ...(props.delete?.requiredPermissions || []),
            ...(props.customActionButtons?.map((button) => (button.requiredPermissions ? button.requiredPermissions : [])).flat() || []),
            ...(props.recovery?.requiredPermissions || [])
        ];
        if (props.permissions && permissions) {
            if (hasAnyPermission(props.permissions, permissions) || permissions.length === 0) {
                result.push(
                    columnHelper.display({
                        header: '',
                        id: 'actions',
                        enableHiding: false,
                        cell: (cellProps: CellContext<T, any>) => {
                            return (
                                <td
                                    data-testid='actions-dropdown-ellipsis'
                                    className='content-center'
                                    onClick={(e) => e.stopPropagation()}>
                                    <Dropdown
                                        label={(<LuEllipsis className='text-brand-primary/70 hover:text-primary text-lg font-bold' />) as any}
                                        buttonStyle='inline-flex justify-center items-center cursor-pointer ps-4 pe-2 py-3 m-0'
                                        hideChevron
                                        requiredPermissions={permissions.length > 0 ? permissions : undefined}>
                                        <DropdownItem
                                            label={CommonMessage.BUTTONS.UPDATE}
                                            icon={ICONS.BUTTON.UPDATE}
                                            onClick={() => props.update?.updateAction && props.update.updateAction(cellProps.row.original)}
                                            classType={ClassType.INFO}
                                            hide={!props.update}
                                            requiredPermissions={props.update?.requiredPermissions}
                                        />
                                        <DropdownItem
                                            label={CommonMessage.BUTTONS.DELETE}
                                            icon={ICONS.BUTTON.DELETE}
                                            onClick={() =>
                                                openModal?.(cellProps.row.original.id, () => (
                                                    <ConfirmationModal
                                                        show={true}
                                                        handleClose={() => closeModal?.(cellProps.row.original.id)}
                                                        onConfirm={() =>
                                                            props.delete?.deleteAction &&
                                                            props.delete
                                                                .deleteAction(
                                                                    ...(props.delete?.deleteKeys
                                                                        ? props.delete.deleteKeys.map((key) => cellProps.row.original[key])!
                                                                        : [])
                                                                )
                                                                .then(() => {
                                                                    closeModal?.(cellProps.row.original.id);
                                                                    props.onDeleteSuccess?.(cellProps.row.original);
                                                                })
                                                                .catch(ErrorHandler.handleError)
                                                        }
                                                        message={CommonMessage.FORMS.DELETE_OBJECT(
                                                            props.delete?.displayName ? (props.delete.displayName(cellProps.row.original) as string) : ''
                                                        )}
                                                        severity={ClassType.DANGER}
                                                        cascadeWarning
                                                    />
                                                ))
                                            }
                                            classType={ClassType.DANGER}
                                            hide={!props.delete}
                                            requiredPermissions={props.delete?.requiredPermissions}
                                        />
                                        <DropdownItem
                                            label={CommonMessage.BUTTONS.RECOVER}
                                            icon={ICONS.BUTTON.RECOVER}
                                            onClick={() => props.recovery?.onRecover && props.recovery.onRecover(cellProps.row.original)}
                                            classType={ClassType.SUCCESS}
                                            hide={!props.recovery || !(cellProps.row.original as any).delete_timestamp}
                                            requiredPermissions={props.recovery?.requiredPermissions}
                                        />
                                        {props.customActionButtons &&
                                            props.customActionButtons.map((button) => (
                                                <DropdownItem
                                                    {...button}
                                                    onClick={() => button.onClick && button.onClick(cellProps.row.original)}
                                                    requiredPermissions={button.requiredPermissions}
                                                    hide={
                                                        button.hide !== undefined
                                                            ? () => (typeof button.hide === 'function' ? button.hide!(cellProps.row.original) : button.hide!)
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                    </Dropdown>
                                </td>
                            );
                        }
                    })
                );
            }
        }
    }

    return result;
}
